// DevPanel: Floating developer panel for debugging, inspired by TanStack Query Devtools.
// Usage: Place <DevPanel onOpenChange={setDevPanelOpen} /> at the root of your app.
// When open, add a right margin/padding to your main app container equal to panelWidth (default 400).
//
// Visibility:
//   The panel requires two conditions to render:
//   1. The panel must be enabled (see below).
//   2. The current user must have the 'developer' role.
//
//   Enabled state is resolved in this order:
//   - localStorage key 'practist:showDevPanel' (if set, takes precedence)
//   - VITE_SHOW_DEV_PANEL_DEFAULT env var (fallback default)
//
//   To always show the panel locally, set VITE_SHOW_DEV_PANEL_DEFAULT=true in .env.local.
//   To toggle in production (or override the default), run from the browser console:
//     enableDevPanel()   — enables and persists across sessions
//     disableDevPanel()  — disables and persists across sessions
//
// Components can register their own debug sections using useDebugSection:
//
// import { useDebugSection } from '@/app/DeveloperPanelContext';
//
// const MyComponent = () => {
//   const [counter, setCounter] = useState(0);
//
//   useDebugSection({
//     title: 'My Component Debug',
//     content: (
//       <div>
//         <div><b>Counter:</b> {counter}</div>
//         <Button size="xs" onClick={() => setCounter(c => c + 1)}>
//           Increment
//         </Button>
//       </div>
//     ),
//     order: 1, // optional: controls section order
//   }, [counter]); // dependencies that trigger re-registration
//
//   return <div>My component content...</div>;
// };

declare global {
  interface Window {
    enableDevPanel?: () => void;
    disableDevPanel?: () => void;
  }
}

import React, { useState, useEffect } from 'react';
import {
  ActionIcon,
  Text,
  Group,
  Divider,
  ScrollArea,
  Stack,
  Code,
  Alert,
} from '@mantine/core';
import { BugIcon, XIcon } from 'lucide-react';
import { useCurrentUser } from '@/database/hooks/hooks';
import { useDeveloperPanelSections } from '@/app/DeveloperPanelContext';
import DemoModeSection from './DemoModeSection';
import FirmDocumentSection from './FirmDocumentSection';
import UserDocumentSection from './UserDocumentSection';
import FirebaseTokenSection from './FirebaseTokenSection';
import BrowserPerformanceSection from './BrowserPerformanceSection';
import EmailProviderSection from './EmailProviderSection';
import { isUsingEmulators } from '@/firebaseConfig';

interface DevPanelProps {
  onOpenChange?: (open: boolean) => void;
  panelWidth?: number;
}

const DEV_PANEL_STORAGE_KEY = 'practist:showDevPanel';

const getDevPanelEnabled = (): boolean => {
  const stored = localStorage.getItem(DEV_PANEL_STORAGE_KEY);
  if (stored !== null) return stored === 'true';
  return import.meta.env.VITE_SHOW_DEV_PANEL_DEFAULT === 'true';
};

export const DevPanel: React.FC<DevPanelProps> = ({
  onOpenChange,
  panelWidth = 400,
}) => {
  const [enabled, setEnabled] = useState(getDevPanelEnabled);
  const [opened, setOpened] = useState(false);
  const { sections } = useDeveloperPanelSections();
  const { currentUser } = useCurrentUser();

  useEffect(() => {
    window.enableDevPanel = () => {
      localStorage.setItem(DEV_PANEL_STORAGE_KEY, 'true');
      setEnabled(true);
    };
    window.disableDevPanel = () => {
      localStorage.setItem(DEV_PANEL_STORAGE_KEY, 'false');
      setEnabled(false);
    };
    return () => {
      delete window.enableDevPanel;
      delete window.disableDevPanel;
    };
  }, []);

  useEffect(() => {
    if (onOpenChange) onOpenChange(opened);
  }, [opened, onOpenChange]);

  if (!enabled) return null;
  if (!currentUser || !currentUser.roles?.includes('developer')) return null;

  // Panel styles
  const asideStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: panelWidth,
    height: '100vh',
    zIndex: 2000,
    background: 'var(--mantine-color-body, #fff)',
    boxShadow: '0 0 24px 0 rgba(0,0,0,0.18)',
    borderLeft: '1px solid #eee',
    display: opened ? 'flex' : 'none',
    flexDirection: 'column',
    pointerEvents: 'auto',
  };

  return (
    <>
      {/* Floating open button */}
      {!opened && (
        <ActionIcon
          variant='filled'
          color='grape'
          size={32}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 'calc(var(--mantine-z-index-overlay) + 1)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
          }}
          onClick={() => setOpened(true)}
          aria-label='Open Dev Panel'
        >
          <BugIcon size={28} />
        </ActionIcon>
      )}
      {/* Panel */}
      <aside style={asideStyle}>
        <Group
          gap='xs'
          align='center'
          justify='space-between'
          style={{ padding: 16, borderBottom: '1px solid #eee' }}
        >
          <Group gap='xs' align='center'>
            <BugIcon size={20} />
            <Text fw={600}>Developer Panel</Text>
          </Group>
          <ActionIcon
            variant='subtle'
            color='gray'
            onClick={() => setOpened(false)}
            aria-label='Close Dev Panel'
          >
            <XIcon size={18} />
          </ActionIcon>
        </Group>
        <Divider mb='sm' />
        <ScrollArea style={{ flex: 1, padding: 16 }}>
          <Stack>
            <Text fw={700} size='lg' mb='sm'>
              Debug Info
            </Text>
            <UserDocumentSection />
            <FirmDocumentSection />
            <FirebaseTokenSection opened={opened} />
            <BrowserPerformanceSection opened={opened} />
            <EmailProviderSection opened={opened} />
            {isUsingEmulators && <DemoModeSection />}
            <Divider />
            {sections.map(
              (section) =>
                section.content && (
                  <div key={section.id}>{section.content}</div>
                ),
            )}
            {sections.length === 0 && (
              <Alert
                color='gray'
                p='sm'
                radius='md'
                title='No custom developer panels registered'
              >
                <Text size='sm'>
                  Components can use <Code>useDebugSection()</Code> to add
                  context-specific info here.
                </Text>
              </Alert>
            )}
          </Stack>
        </ScrollArea>
      </aside>
    </>
  );
};
